// Parlay builder — slide-up sheet that lists legs, lets user edit, and
// prices the parlay using the backend Gaussian-copula ParlayPricer.

import React, { useMemo, useState } from "react";
import { Loader2, Trash2, Calculator, X, AlertTriangle, Share2, Plus, Minus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { useParlay, MIN_LEGS, MAX_LEGS } from "@/lib/parlay";
import { buildSlipUrl } from "@/lib/parlayShare";
import { copyText } from "@/lib/share";
import { useSportsbookLinks, trackAffiliateClick } from "@/lib/affiliateTracking";
import PickerPanel from "@/components/ParlayPicker";

const ACCENT = "#067647";

function decToAmerican(dec) {
  if (!dec || dec <= 1) return null;
  const v = dec >= 2 ? Math.round((dec - 1) * 100) : Math.round(-100 / (dec - 1));
  return v > 0 ? `+${v}` : `${v}`;
}

function LegPill({ leg, onRemove, index, resolved }) {
  const isProp = leg.kind === "prop";
  const pickedTeam = isProp ? leg.player : (leg.side === "home" ? leg.home_team : leg.away_team);
  const other = leg.side === "home" ? leg.away_team : leg.home_team;
  const dec = resolved?.odds_decimal || leg.odds_decimal;
  const modelActive = !!resolved?.model_active;
  const dateLabel = (leg.date || "").slice(0, 10) || "TBD";
  return (
    <div
      className="flex items-start justify-between gap-3 border-2 border-[#111] p-3 bg-white"
      data-testid={`parlay-leg-${index}`}
    >
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#555] font-bold mb-1">
          {leg.sport} · {dateLabel}
          {isProp && (
            <span
              className="ml-2 px-1.5 py-0.5 text-[8px] font-extrabold"
              style={{ background: "#111", color: "#fff", letterSpacing: "0.15em" }}
            >
              PROP
            </span>
          )}
          {modelActive && (
            <span
              className="ml-2 px-1.5 py-0.5 text-[8px] font-extrabold"
              style={{ background: ACCENT, color: "#fff", letterSpacing: "0.15em" }}
            >
              MODEL
            </span>
          )}
        </div>
        <div className="font-display font-extrabold text-[#111] text-base leading-tight">
          {isProp
            ? <>{leg.player} <span className="opacity-70">· {leg.market_label} {leg.side.toUpperCase()} {leg.point}</span></>
            : <>{pickedTeam} ML</>}
        </div>
        <div className="font-mono text-[10px] tracking-[0.1em] uppercase text-[#888] mt-0.5">
          {isProp
            ? <>{leg.away_team} @ {leg.home_team}</>
            : <>vs {other} · {leg.side === "home" ? "home" : "away"}</>}
        </div>
        {resolved ? (
          <div className="font-mono text-xs mt-1.5 flex gap-3 flex-wrap text-[#222] font-bold">
            <span>{decToAmerican(dec)} ({dec?.toFixed(2)})</span>
            {resolved.best_book && <span className="text-[#888]">@ {resolved.best_book}</span>}
            <span className="text-[#888]">
              model {(resolved.model_prob * 100).toFixed(1)}%
            </span>
          </div>
        ) : dec ? (
          <div className="font-mono text-xs mt-1.5 flex gap-3 flex-wrap text-[#222] font-bold">
            <span>{decToAmerican(dec)} ({Number(dec).toFixed(2)})</span>
          </div>
        ) : (
          <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#888] mt-1.5">
            price will resolve on calculate
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onRemove(leg)}
        className="shrink-0 p-1.5 border-2 border-[#111] hover:bg-[#111] hover:text-white transition-colors"
        aria-label="Remove leg"
        data-testid={`parlay-leg-remove-${index}`}
      >
        <Trash2 size={14} strokeWidth={2.6} />
      </button>
    </div>
  );
}

function PricingResult({ pricing, kelly, bankroll }) {
  if (!pricing) return null;
  const isPlay = pricing.recommendation === "play";
  const evPct = pricing.ev_pct * 100;
  const stakeDollars = (kelly?.bet_size_pct || 0) * bankroll;
  const payoutDollars = stakeDollars * pricing.payout;
  const correlationUplift = pricing.correlation_adjustment;

  return (
    <div className="mt-6 border-2 border-[#111] bg-[#fafaf9]" data-testid="parlay-pricing-result">
      <div
        className="flex items-center justify-between px-4 py-3 border-b-2 border-[#111]"
        style={{ background: isPlay ? ACCENT : "#111", color: "#fff" }}
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase font-extrabold">
          Verdict
        </span>
        <span className="font-display text-lg font-extrabold uppercase tracking-wider" data-testid="parlay-verdict">
          {isPlay ? `PLAY · ${evPct >= 0 ? "+" : ""}${evPct.toFixed(2)}% EV` : "PASS"}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
        <StatBlock label="Payout" value={`${pricing.payout.toFixed(2)}×`} testid="parlay-stat-payout" />
        <StatBlock
          label="Hit %"
          value={`${(pricing.joint_prob_model * 100).toFixed(1)}%`}
          sub={`Book implies ${(pricing.joint_prob_book * 100).toFixed(1)}%`}
          testid="parlay-stat-hit"
        />
        <StatBlock
          label="Expected Value"
          value={`${evPct >= 0 ? "+" : ""}${evPct.toFixed(2)}%`}
          sub={`per $1 risked`}
          testid="parlay-stat-ev"
        />
        <StatBlock
          label="Suggested Stake"
          value={stakeDollars > 0 ? `$${stakeDollars.toFixed(2)}` : "—"}
          sub={stakeDollars > 0
            ? `wins $${(payoutDollars - stakeDollars).toFixed(2)}`
            : (kelly?.reason === "no_edge" ? "no edge" : (kelly?.reason === "stop_loss" ? "stop-loss" : ""))}
          testid="parlay-stat-stake"
        />
      </div>
      {typeof correlationUplift === "number" && Math.abs(correlationUplift - 1) > 0.01 && (
        <div className="px-4 pb-3 text-xs text-[#555] font-mono tracking-[0.05em] italic">
          Correlation adjustment: {correlationUplift.toFixed(3)}× vs independence.
        </div>
      )}
      <div className="px-4 pb-4 text-[10px] font-mono tracking-[0.15em] uppercase text-[#666]">
        {pricing.correlation_note}
      </div>
    </div>
  );
}

function StatBlock({ label, value, sub, testid }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#888] font-bold">
        {label}
      </span>
      <span className="font-display font-extrabold text-lg md:text-xl text-[#111] leading-none">
        {value}
      </span>
      {sub && (
        <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-[#777] font-bold">
          {sub}
        </span>
      )}
    </div>
  );
}

export default function ParlayBuilder() {
  const {
    legs, open, setOpen,
    bankroll, setBankroll,
    pricing, pricingLoading, pricingError,
    removeLeg, clearLegs, priceParlay,
  } = useParlay();
  const sportsbooks = useSportsbookLinks();
  const [pickerOpen, setPickerOpen] = useState(false);

  async function handleShareSlip() {
    const url = buildSlipUrl(legs);
    try { window.history.replaceState({}, "", url); } catch {}
    const ok = await copyText(url);
    if (ok) {
      toast.success("Parlay link copied — share it anywhere.");
    } else {
      toast("Copy failed — here's the link:", { description: url });
    }
  }

  const resolvedMap = useMemo(() => {
    const m = new Map();
    for (const r of (pricing?.legs_resolved || [])) {
      let k;
      if (r.kind === "prop") {
        k = [
          "prop",
          (r.sport || "").toLowerCase(),
          (r.event_id || "").toLowerCase(),
          (r.market_key || "").toLowerCase(),
          (r.player || "").toLowerCase(),
          String(r.point ?? ""),
          (r.side || "").toLowerCase(),
        ].join("|");
      } else {
        k = [
          "ml",
          (r.sport || "").toLowerCase(),
          (r.home_team || "").toLowerCase(),
          (r.away_team || "").toLowerCase(),
          (r.date || "tbd").toLowerCase().slice(0, 10),
          (r.side || "").toLowerCase(),
        ].join("|");
      }
      m.set(k, r);
    }
    return m;
  }, [pricing]);

  const unresolved = pricing?.unresolved_legs || [];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="bottom"
        className="max-h-[90vh] overflow-y-auto bg-[#f6f6f4] border-t-4 border-[#111] rounded-t-none"
        data-testid="parlay-builder-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-display font-extrabold text-2xl md:text-3xl text-[#111] uppercase tracking-tight">
            Parlay Builder
          </SheetTitle>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold">
            Gaussian-copula EV · Fractional-Kelly stake
          </p>
        </SheetHeader>

        <div className="mt-5 max-w-5xl mx-auto">
          {/* Bankroll + action row */}
          <div className="flex flex-wrap items-end gap-4 border-2 border-[#111] p-3 bg-white mb-5">
            <label className="flex flex-col gap-1">
              <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#555] font-bold">
                Bankroll ($)
              </span>
              <input
                type="number"
                min={1}
                step={10}
                value={bankroll}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v) && v > 0) setBankroll(v);
                }}
                className="border-2 border-[#111] bg-transparent px-3 py-1.5 font-display text-lg font-extrabold w-32 focus-ring"
                data-testid="parlay-bankroll-input"
              />
            </label>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => priceParlay()}
              disabled={pricingLoading || legs.length < MIN_LEGS}
              className="inline-flex items-center gap-2 px-5 py-3 font-display font-extrabold uppercase tracking-[0.15em] text-sm border-2 focus-ring active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: ACCENT, color: "#fff", borderColor: ACCENT }}
              data-testid="parlay-calculate-btn"
            >
              {pricingLoading
                ? <><Loader2 size={16} className="spin-slow" /> Pricing…</>
                : <><Calculator size={16} strokeWidth={2.6} /> Calculate parlay</>}
            </button>
            <button
              type="button"
              onClick={handleShareSlip}
              disabled={legs.length < MIN_LEGS}
              className="inline-flex items-center gap-2 px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase font-bold border-2 border-[#111] text-[#111] hover:bg-[#111] hover:text-white transition-colors focus-ring disabled:opacity-30"
              data-testid="parlay-share-btn"
              title={legs.length < MIN_LEGS ? `Add at least ${MIN_LEGS} legs to share` : "Copy a shareable link to this parlay slip"}
            >
              <Share2 size={14} strokeWidth={2.6} />
              Share slip
            </button>
            <button
              type="button"
              onClick={clearLegs}
              disabled={legs.length === 0}
              className="inline-flex items-center gap-2 px-4 py-3 font-mono text-[10px] tracking-[0.25em] uppercase font-bold border-2 border-[#111] text-[#111] hover:bg-[#111] hover:text-white transition-colors focus-ring disabled:opacity-30"
              data-testid="parlay-clear-btn"
            >
              <X size={14} strokeWidth={2.6} />
              Clear all
            </button>
          </div>

          {/* Add legs picker toggle */}
          <div className="mb-5">
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className="inline-flex items-center gap-2 px-4 py-2.5 font-mono text-[10px] tracking-[0.25em] uppercase font-extrabold border-2 border-[#111] text-[#111] hover:bg-[#111] hover:text-white transition-colors focus-ring active:translate-y-px"
              data-testid="parlay-picker-toggle"
              aria-expanded={pickerOpen}
            >
              {pickerOpen ? <Minus size={14} strokeWidth={2.6} /> : <Plus size={14} strokeWidth={2.6} />}
              {pickerOpen ? "Close picker" : "Add legs from upcoming games"}
            </button>
          </div>
          {pickerOpen && <PickerPanel />}

          {/* Legs list */}
          {legs.length === 0 ? (
            <div className="border-2 border-dashed border-[#aaa] p-8 text-center" data-testid="parlay-empty-state">
              <p className="font-display font-extrabold text-xl text-[#111] uppercase tracking-tight">
                No legs yet
              </p>
              <p className="font-mono text-xs text-[#555] mt-2 tracking-[0.1em]">
                Open a game's betting insights and tap <span className="font-bold text-[#111]">+ Parlay</span> on
                the moneyline side you like. {MIN_LEGS}–{MAX_LEGS} legs supported.
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="parlay-legs-list">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold">
                  {legs.length} / {MAX_LEGS} legs
                </span>
              </div>
              {legs.map((leg, i) => {
                let k;
                if (leg.kind === "prop") {
                  k = [
                    "prop",
                    (leg.sport || "").toLowerCase(),
                    (leg.event_id || "").toLowerCase(),
                    (leg.market_key || "").toLowerCase(),
                    (leg.player || "").toLowerCase(),
                    String(leg.point ?? ""),
                    (leg.side || "").toLowerCase(),
                  ].join("|");
                } else {
                  k = [
                    "ml",
                    (leg.sport || "").toLowerCase(),
                    (leg.home_team || "").toLowerCase(),
                    (leg.away_team || "").toLowerCase(),
                    (leg.date || "tbd").toLowerCase().slice(0, 10),
                    (leg.side || "").toLowerCase(),
                  ].join("|");
                }
                return (
                  <LegPill
                    key={k}
                    leg={leg}
                    onRemove={removeLeg}
                    index={i}
                    resolved={resolvedMap.get(k)}
                  />
                );
              })}
            </div>
          )}

          {/* Unresolved warnings */}
          {unresolved.length > 0 && (
            <div
              className="mt-4 border-2 border-[#c0272d] bg-[#fff5f5] p-3 flex gap-3"
              data-testid="parlay-unresolved-warning"
            >
              <AlertTriangle size={18} strokeWidth={2.6} className="text-[#c0272d] shrink-0 mt-0.5" />
              <div className="font-mono text-xs text-[#c0272d] tracking-[0.05em]">
                <span className="font-extrabold uppercase tracking-[0.2em]">
                  {unresolved.length} leg{unresolved.length === 1 ? "" : "s"} couldn't be priced:
                </span>
                <ul className="mt-1 ml-4 list-disc space-y-0.5">
                  {unresolved.map((u, i) => (
                    <li key={i}>
                      {u.away_team} @ {u.home_team} ({u.sport}) — {u.reason}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Error */}
          {pricingError && (
            <div
              className="mt-4 border-2 border-[#c0272d] bg-[#fff5f5] p-3 font-mono text-xs text-[#c0272d] tracking-[0.05em] font-bold"
              data-testid="parlay-error"
            >
              {pricingError}
            </div>
          )}

          {/* Pricing */}
          <PricingResult pricing={pricing?.pricing} kelly={pricing?.kelly} bankroll={bankroll} />

          {/* Place this parlay — sportsbook CPA row. Only after pricing to
              keep the funnel visible: user prices, sees edge, clicks book. */}
          {pricing?.pricing && Object.keys(sportsbooks).length > 0 && (
            <div
              className="mt-6 border-2 border-[#111] p-4 bg-white"
              data-testid="parlay-sportsbook-row"
            >
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#111] font-extrabold mb-2">
                Place this parlay
                <span className="normal-case tracking-normal text-[#888] font-normal ml-2">
                  21+ · For entertainment only · 1-800-GAMBLER
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(sportsbooks).map(([partner, url]) => (
                  <a
                    key={partner}
                    href={url}
                    target="_blank"
                    rel="sponsored noopener noreferrer"
                    onClick={() => trackAffiliateClick({
                      partner,
                      kind: "sportsbook",
                      context: "parlay-slip",
                    })}
                    data-testid={`parlay-sportsbook-${partner.toLowerCase().replace(/\s+/g, '-')}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase font-extrabold border-2 border-[#111] text-[#111] hover:bg-[#111] hover:text-white transition-colors focus-ring active:translate-y-px no-underline"
                    title={`Build this parlay on ${partner}`}
                  >
                    {partner}
                    <ExternalLink size={10} strokeWidth={2.8} />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 font-mono text-[10px] tracking-[0.15em] uppercase text-[#888] font-bold text-center pb-4">
            For entertainment only. 21+. Gambling problem? Call 1-800-GAMBLER.
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
